// Signature pad (R-144 / LOG-005 — the L1 proof medium). A canvas the operator
// signs with pointer/touch; the imperative handle exposes the PNG data-URL and
// a clear. Shared by the driver POD (Logistics) and the custodian handover
// (asset deploy). Behaviour is 1:1 with the original inline Logistics version.
import { useEffect, useRef } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { Button } from '../Button/button';
import { styles } from './signature-pad.styles';
export interface SignatureHandle {
  /** The signed PNG as a data-URL, or '' when the pad is untouched. */
  toDataUrl(): string;
  clear(): void;
}
interface SignaturePadProps {
  handleRef: {
    current: SignatureHandle | null;
  };
  clearLabel: string;
}
export function SignaturePad({
  handleRef,
  clearLabel
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    const pos = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - r.left) * canvas.width / r.width,
        y: (e.clientY - r.top) * canvas.height / r.height
      };
    };
    const down = (e: PointerEvent) => {
      drawing.current = true;
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      canvas.setPointerCapture(e.pointerId);
    };
    const moveH = (e: PointerEvent) => {
      if (!drawing.current) return;
      const p = pos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      dirty.current = true;
    };
    const up = () => {
      drawing.current = false;
    };
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', moveH);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointerleave', up);
    handleRef.current = {
      toDataUrl: () => dirty.current ? canvas.toDataURL('image/png') : '',
      clear: () => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        dirty.current = false;
      }
    };
    return () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', moveH);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointerleave', up);
      handleRef.current = null;
    };
  }, [handleRef]);
  return <div>
      <canvas ref={canvasRef} width={400} height={140} {...stylex.props(styles.signatureBox)} />
      <Button variant="ghost" size="sm" onClick={() => handleRef.current?.clear()}>
        {clearLabel}
      </Button>
    </div>;
}